import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Gift, Truck, Users, Sparkles, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { unifiedAPI } from '../../utils/api';

const SwagHome = () => {
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch categories and featured products from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch categories and featured products in parallel
        const [categoriesRes, productsRes] = await Promise.all([
          unifiedAPI.getCategories({ product_type: 'swag', include_counts: true }),
          unifiedAPI.getProducts({ product_type: 'swag', limit: 8, sort: 'name_asc' })
        ]);
        
        setCategories(categoriesRes.categories || []);
        setFeaturedProducts(productsRes.products || []);
      } catch (error) {
        console.error('Error fetching swag data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-ivory-50">
      {/* Hero Section - Luxury styling */}
      <section className="relative overflow-hidden gradient-hero">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-gold-500/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-gold-400/5 rounded-full blur-[100px]" />
        
        <div className="relative container mx-auto px-4 pt-20 pb-24 md:pt-28 md:pb-32">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-500/10 border border-gold-500/20 mb-8">
              <Gift className="w-4 h-4 text-gold-600" />
              <span className="text-gold-700 text-sm font-medium font-body">Custom Branded Promotional Products</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-heading font-semibold mb-6 leading-[1.1]">
              <span className="text-ivory-800">Promotional</span>
              <br />
              <span className="gradient-text-gold">Swag</span>
            </h1>
            
            <p className="text-lg md:text-xl text-ivory-500 max-w-2xl mx-auto mb-10 leading-relaxed font-body">
              Elevate your brand with premium promotional products. From custom drinkware to executive gift sets, make a lasting impression.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg" 
                className="text-lg px-8 py-6 btn-luxury-primary rounded-lg font-body"
                asChild
              >
                <Link to="/Swag/products" data-testid="browse-all-swag">
                  Browse All Products
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 py-6 btn-luxury-outline rounded-lg font-body"
                asChild
              >
                <Link to="/contact">
                  Request Quote
                </Link>
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center gap-8 mt-12">
              <div className="flex items-center gap-2 text-ivory-500">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm font-body">No Minimum Orders</span>
              </div>
              <div className="flex items-center gap-2 text-ivory-500">
                <Truck className="w-5 h-5 text-gold-600" />
                <span className="text-sm font-body">Fast Turnaround</span>
              </div>
              <div className="flex items-center gap-2 text-ivory-500">
                <Users className="w-5 h-5 text-gold-600" />
                <span className="text-sm font-body">Bulk Discounts</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section - Stone gradient */}
      <section className="py-20 gradient-stone">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800 mb-4">
              Shop by Category
            </h2>
            <p className="text-ivory-500 text-lg font-body">
              Find the perfect promotional items for your brand
            </p>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-10 h-10 text-gold-500 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {categories.slice(0, 6).map((category) => (
                <Link
                  key={category.id}
                  to={`/Swag/products/${category.id}`}
                  className="group relative aspect-[4/3] rounded-xl overflow-hidden shadow-luxury-lg"
                  data-testid={`swag-category-${category.id}`}
                >
                  <img
                    src={category.image || 'https://placehold.co/400x300/f5f3f0/9a8b7d?text=Category'}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => { e.target.src = 'https://placehold.co/400x300/f5f3f0/9a8b7d?text=Category'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ivory-900/80 via-ivory-900/40 to-transparent" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <h3 className="text-2xl font-heading font-semibold text-white mb-2 group-hover:text-gold-300 transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-ivory-200 text-sm mb-4 line-clamp-2 font-body">
                      {category.description || `Browse our ${category.name.toLowerCase()} collection`}
                    </p>
                    <div className="flex items-center text-gold-400 font-medium text-sm opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 font-body">
                      <span>Shop Now ({category.count || 0} products)</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Products - Pearl gradient */}
      <section className="py-20 gradient-pearl">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800 mb-4">
                Featured Products
              </h2>
              <p className="text-ivory-500 text-lg font-body">
                Our most popular promotional items
              </p>
            </div>
            <Button 
              variant="ghost" 
              className="text-gold-600 hover:text-gold-700 hover:bg-gold-500/10 mt-4 md:mt-0 font-body"
              asChild
            >
              <Link to="/Swag/products">
                View All Products
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-10 h-10 text-gold-500 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.slice(0, 8).map((product) => (
                <Link
                  key={product.id}
                  to={`/Swag/product/${product.id}`}
                  className="group"
                  data-testid={`featured-swag-${product.id}`}
                >
                  <div className="h-full rounded-xl bg-ivory-50 border border-ivory-300/50 overflow-hidden card-luxury">
                    <div className="aspect-square overflow-hidden bg-ivory-200">
                      <img
                        src={product.image || 'https://placehold.co/300x300/f5f3f0/9a8b7d?text=Product'}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.src = 'https://placehold.co/300x300/f5f3f0/9a8b7d?text=Product'; }}
                      />
                    </div>
                    <div className="p-5">
                      <p className="text-xs text-gold-600 font-medium mb-1 font-body">{product.sku || product.id}</p>
                      <h3 className="font-heading font-semibold text-ivory-800 mb-2 group-hover:text-gold-700 transition-colors line-clamp-2">
                        {product.name}
                      </h3>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xl font-bold text-gold-600 font-body">
                          ${(product.basePrice || 0).toFixed(2)}
                        </span>
                        <span className="text-xs text-ivory-500 font-body">min {product.minQuantity || 1} pcs</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-ivory-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800 mb-4">
              Why Choose Engraveca for Promo Products?
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { icon: Sparkles, title: 'Premium Quality', desc: 'Only the best branded merchandise for your company' },
              { icon: Truck, title: 'Fast Delivery', desc: 'Quick turnaround times to meet your deadlines' },
              { icon: Users, title: 'Bulk Pricing', desc: 'Volume discounts that make sense for your budget' },
              { icon: Gift, title: 'Custom Branding', desc: 'Your logo on high-quality promotional items' },
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 rounded-xl bg-ivory-100 border border-gold-500/20 flex items-center justify-center mx-auto mb-6 group-hover:border-gold-500/40 group-hover:scale-105 transition-all duration-300 shadow-luxury">
                  <item.icon className="w-8 h-8 text-gold-600" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-ivory-800 mb-3">{item.title}</h3>
                <p className="text-ivory-500 text-sm font-body">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Elegant champagne gold */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold-700 via-gold-600 to-gold-500" />
        
        <div className="relative container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-semibold text-white mb-6">
            Ready to Brand Your Business?
          </h2>
          <p className="text-xl text-gold-100 mb-10 max-w-2xl mx-auto font-body">
            Get custom promotional products that make a lasting impression
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="text-lg px-8 py-6 bg-white text-gold-700 hover:bg-ivory-100 rounded-lg shadow-luxury-lg font-body font-semibold"
              asChild
            >
              <Link to="/Swag/products" data-testid="cta-shop-now">
                Shop Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="text-lg px-8 py-6 border-2 border-white/30 bg-transparent text-white hover:bg-white/10 rounded-lg font-body"
              asChild
            >
              <Link to="/contact">
                Contact Sales
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SwagHome;
